import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Phone, MapPin, Calendar, TrendingUp, Package, DollarSign, Pencil, Save, X, Trash2, Clock, Plus } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const COMPETITOR_OPTIONS = ['Invisalign', 'Ormco Spark', 'Dentcare Aligners', 'Smile Aligners', 'Illusion Aligners'];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountDetail = ({ onLogout }) => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [account, setAccount] = useState(null);
  const [salesHistory, setSalesHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit account state
  const [editingAccount, setEditingAccount] = useState(false);
  const [showCallTypePicker, setShowCallTypePicker] = useState(false);
  const [editAccountData, setEditAccountData] = useState({ name: '', address: '', city: '', pincode: '', clinic_potential: '', competitor_brands: [], competitor_brands_others: '', current_sales_value: 0 });
  const [savingAccount, setSavingAccount] = useState(false);

  // Edit contact state
  const [editingContact, setEditingContact] = useState(null);
  const [editContactData, setEditContactData] = useState({});
  const [savingContact, setSavingContact] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [newContactData, setNewContactData] = useState({ name: '', designation: '', whatsapp_number: '', email: '', can_influence_decisions: '' });

  useEffect(() => {
    fetchAccountData();
  }, [accountId]);

  const fetchAccountData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [accountRes, historyRes] = await Promise.all([
        axios.get(`${API}/accounts/${accountId}`, { headers }),
        axios.get(`${API}/accounts/${accountId}/sales-history`, { headers })
      ]);
      setAccount(accountRes.data);
      setSalesHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to load account details');
      navigate('/accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAccount = async () => {
    // Always re-fetch fresh data from DB before opening modal
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fresh = res.data;
      setAccount(fresh);
      setEditAccountData({
        name: fresh.name || '',
        address: fresh.address || '',
        city: fresh.city || '',
        pincode: fresh.pincode || '',
        clinic_potential: (fresh.clinic_potential || '').trim(),
        competitor_brands: fresh.competitor_brands || [],
        competitor_brands_others: fresh.competitor_brands_others || '',
        current_sales_value: fresh.current_sales_value || 0
      });
    } catch {
      // Fallback to cached state
      setEditAccountData({
        name: account.name || '',
        address: account.address || '',
        city: account.city || '',
        pincode: account.pincode || '',
        clinic_potential: (account.clinic_potential || '').trim(),
        competitor_brands: account.competitor_brands || [],
        competitor_brands_others: account.competitor_brands_others || '',
        current_sales_value: account.current_sales_value || 0
      });
    }
    setEditingAccount(true);
  };

  const handleSaveAccount = async () => {
    if (!editAccountData.name || !editAccountData.address) { toast.error('Name and address are required'); return; }
    if (!editAccountData.city) { toast.error('City is required'); return; }
    if (!editAccountData.clinic_potential) { toast.error('Clinic Potential is required'); return; }
    // competitor_brands now optional — removed mandatory check
    setSavingAccount(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Save mandatory fields directly to DB (no approval for first-time fills)

      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      if (isAdmin) {
        await axios.put(`${API}/accounts/${accountId}/fill-missing-fields`, {
          name: editAccountData.name, address: editAccountData.address,
          city: editAccountData.city, pincode: editAccountData.pincode,
          clinic_potential: editAccountData.clinic_potential,
          competitor_brands: editAccountData.competitor_brands,
          competitor_brands_others: editAccountData.competitor_brands_others || '',
        }, { headers });
        const freshRes = await axios.get(`${API}/accounts/${accountId}`, { headers });
        setAccount(freshRes.data);
        setEditingAccount(false);
        toast.success('Account updated successfully! ✅');
      } else {
        await axios.put(`${API}/accounts/${accountId}/request-change`, editAccountData, { headers });
        setEditingAccount(false);
        toast.success('Changes submitted for admin approval 🕐');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(`⚠️ Delete "${account.name}"?\n\nThis will permanently delete the account and ALL its sales calls.\n\nThis CANNOT be undone.`)) return;
    const typed = window.prompt(`Type the account name to confirm deletion:\n\n"${account.name}"`);
    if (typed !== account.name) { toast.error('Account name did not match — deletion cancelled'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account deleted successfully');
      navigate('/accounts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account. Admin access required.');
    }
  };

  const handleAddContact = async () => {
    if (!newContactData.name || !newContactData.designation || !newContactData.whatsapp_number) {
      toast.error('Name, designation, and phone number are required');
      return;
    }
    setAddingContact(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/accounts/${accountId}/contacts`, {
        ...newContactData,
        can_influence_decisions: '',
        email: newContactData.email || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact added successfully!');
      setShowAddContact(false);
      setNewContactData({ name: '', designation: '', whatsapp_number: '', email: '', can_influence_decisions: '' });
      fetchAccountData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    } finally {
      setAddingContact(false);
    }
  };

  const handleEditContact = (contact) => {
    setEditContactData({
      name: contact.name || '',
      designation: contact.designation || '',
      whatsapp_number: contact.whatsapp_number || '',
      email: contact.email || '',
      can_influence_decisions: contact.can_influence_decisions || ''
    });
    setEditingContact(contact.id);
  };

  const handleSaveContact = async () => {
    if (!editContactData.name || !editContactData.designation || !editContactData.whatsapp_number) {
      toast.error('Name, designation, and WhatsApp number are required');
      return;
    }
    setSavingContact(true);
    try {
      const token = localStorage.getItem('token');
      const contactRes = await axios.put(
        `${API}/accounts/${accountId}/contacts/${editingContact}/request-change`,
        editContactData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (contactRes.data.approval_status === 'pending') {
        toast.success('Contact changes submitted for admin approval! 🕐');
      } else {
        toast.success('Contact updated successfully!');
      }
      setEditingContact(null);
      fetchAccountData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  const DESIGNATIONS = ['Clinic Owner (Orthodontist)', 'Clinic Owner (Non-Orthodontist)', 'Consultant (Orthodontist)', 'Consultant (Non-Orthodontist)', 'Others'];

  if (loading) {
    return (
      <Layout onLogout={onLogout}>
        <div className="p-8 flex items-center justify-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!account) return null;

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="account-detail-page">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/accounts')}
            variant="ghost"
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Accounts
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-mono bg-gradient-to-r from-teal-100 to-emerald-100 text-teal-700 px-3 py-1 rounded-lg font-semibold">
                  {account.account_id}
                </span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {account.name}
              </h1>
              <p className="text-base text-slate-600 mt-2 flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                {account.address}{account.city ? `, ${account.city}` : ''}
              </p>
              {account.created_by && (
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                  <span className="text-xs">👤 Created by:</span>
                  <strong className="text-xs text-slate-700">{account.created_by_name || account.created_by}</strong>
                </p>
              )}
              {account.clinic_potential && (
                <p className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                  <span className="font-medium">Clinic Potential:</span>
                  <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${
                    account.clinic_potential === 'High' ? 'bg-emerald-100 text-emerald-700' :
                    account.clinic_potential === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>{account.clinic_potential}</span>
                </p>
              )}
              {account.competitor_brands?.length > 0 && (
                <div className="mt-2">
                  <span className="text-sm font-medium text-slate-600">Competitor Brands: </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {account.competitor_brands.map(b => (
                      <span key={b} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-lg">{b}</span>
                    ))}
                  </div>
                </div>
              )}

            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleEditAccount}
                variant="outline"
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                data-testid="edit-account-button"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit Account
              </Button>
              <Button
                onClick={handleDeleteAccount}
                style={{ display: isAdmin ? 'flex' : 'none' }}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="delete-account-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
              <Button
                onClick={() => navigate(`/calls/new?accountId=${accountId}`)}
                className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white hover:from-teal-800 hover:to-emerald-600"
                data-testid="new-call-button"
              >
                <Phone className="mr-2 h-4 w-4" />
                Register New Call
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-teal-50 rounded-lg">
                <Calendar className="h-6 w-6 text-teal-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Total Visits</p>
                <p className="text-3xl font-bold text-slate-900" data-testid="visit-count">
                  {salesHistory?.visit_count || 0}
                </p>
              </div>
            </div>
          </div>

          <div className={`rounded-xl border shadow-sm p-6 ${
            account.current_sales_value > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-100'
          }`}>
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-lg ${account.current_sales_value > 0 ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                <DollarSign className={`h-6 w-6 ${account.current_sales_value > 0 ? 'text-emerald-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Current Sales (Rs.)</p>
                <p className={`text-3xl font-bold ${account.current_sales_value > 0 ? 'text-emerald-700' : 'text-slate-900'}`} data-testid="current-sales-value">
                  {account.current_sales_value > 0 ? `Rs.${account.current_sales_value.toLocaleString()}` : 'Rs.0'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Orders (Zoho)</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="orders-received">
                  {account.orders_received > 0 ? `Rs.${account.orders_received.toLocaleString()}` : 'Pending'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-amber-50 rounded-lg">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 uppercase tracking-wide font-medium">Contacts</p>
                <p className="text-3xl font-bold text-slate-900">
                  {account.contacts?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contacts Section */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm mb-8">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Contact Persons
            </h2>
            <Button
              onClick={() => setShowAddContact(true)}
              variant="outline"
              className="border-teal-300 text-teal-700 hover:bg-teal-50 rounded-xl text-sm font-semibold"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Contact
            </Button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contacts-list">
              {account.contacts?.map((contact) => (
                <div key={contact.id} className="p-4 border border-slate-200 rounded-xl hover:border-teal-200 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">{contact.name}</h3>
                      <p className="text-sm text-slate-600">{contact.designation}</p>
                      <div className="mt-2 space-y-1 text-sm">
                        <span className="text-slate-500 block">
                          <Phone className="h-3 w-3 inline mr-1" />
                          {contact.whatsapp_number}
                        </span>
                        {contact.email && (
                          <span className="text-slate-500 block">
                            <span className="inline-block w-3 h-3 mr-1 text-center">@</span>
                            {contact.email}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {contact.can_influence_decisions === 'High' ? (
                          <span className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-100">High Influence</span>
                        ) : contact.can_influence_decisions === 'Medium' ? (
                          <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded-lg border border-amber-100">Medium Influence</span>
                        ) : contact.can_influence_decisions === 'Low' ? (
                          <span className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded-lg border border-red-100">Low Influence</span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-amber-50 text-amber-700 rounded-lg border border-amber-200">Decision: TBD</span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleEditContact(contact)}
                      variant="ghost"
                      size="sm"
                      className="text-teal-700 hover:text-teal-700 hover:bg-teal-50"
                      data-testid="edit-contact-button"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sales Call History */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sales Call History
            </h2>
          </div>
          <div className="p-6" data-testid="sales-history">
            {salesHistory?.sales_calls?.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No sales calls recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-100">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Contact</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Category / Purpose</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Follow-Up Action</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Follow-Up Date</th>
                      <th className="py-3 px-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {salesHistory?.sales_calls?.map((call, index) => {
                      let dateStr = '';
                      try { dateStr = format(parseISO(call.call_date), 'dd MMM yy'); } catch { dateStr = call.call_date || ''; }
                      const followUpDate = call.follow_up_date || call.next_visit_date || '';
                      let followUpStr = '';
                      try { followUpStr = followUpDate ? format(parseISO(followUpDate), 'dd MMM yy') : '—'; } catch { followUpStr = followUpDate || '—'; }
                      const category = call.call_category || (call.telephonic_purposes?.join(', ')) || '—';
                      const followUpActions = call.follow_up_actions?.join(', ') || '—';
                      const callType = call.call_type || 'Field Call';
                      return (
                        <tr key={call.id} className="hover:bg-teal-50/30 transition-colors">
                          <td className="py-3 px-3 whitespace-nowrap text-xs font-medium text-slate-700">{dateStr}</td>
                          <td className="py-3 px-3">
                            <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${callType.includes('Telephonic') ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'}`}>
                              {callType.includes('Telephonic') ? '📞 Telephonic' : '🤝 Field'}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-xs text-slate-600">{call.contact_person_name || '—'}</td>
                          <td className="py-3 px-3">
                            <span className="text-xs px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 font-medium">
                              {category.length > 35 ? category.slice(0, 35) + '...' : category}
                            </span>
                          </td>
                          <td className="py-3 px-3 max-w-[200px] text-xs text-slate-500">
                            {followUpActions.length > 40 ? followUpActions.slice(0, 40) + '...' : followUpActions}
                          </td>
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span className={`text-xs font-semibold ${followUpStr !== '—' ? 'text-emerald-600' : 'text-slate-300'}`}>
                              {followUpStr}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            {isAdmin && (
                              <button type="button"
                                onClick={async () => {
                                  if (!window.confirm('Delete this call? This cannot be undone.')) return;
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.delete(`${BACKEND_URL}/api/sales-calls/${call.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                    toast.success('Call deleted ✅');
                                    const r = await axios.get(`${BACKEND_URL}/api/accounts/${accountId}/sales-history`, { headers: { Authorization: `Bearer ${token}` } });
                                    setSalesHistory(r.data);
                                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                                }}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Edit Account Modal */}
        <Dialog open={editingAccount} onOpenChange={setEditingAccount}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Edit Account</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Account Name *</Label>
                <Input value={editAccountData.name} onChange={e => setEditAccountData(p => ({ ...p, name: e.target.value }))} className="mt-1 h-12" data-testid="edit-account-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Address *</Label>
                <Textarea value={editAccountData.address} onChange={e => setEditAccountData(p => ({ ...p, address: e.target.value }))} className="mt-1" data-testid="edit-account-address" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">City *</Label>
                <Input value={editAccountData.city} onChange={e => setEditAccountData(p => ({ ...p, city: e.target.value }))} className="mt-1 h-12" placeholder="City name" data-testid="edit-account-city" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Pincode *</Label>
                <Input value={editAccountData.pincode || ''} onChange={e => setEditAccountData(p => ({ ...p, pincode: e.target.value }))} className="mt-1 h-12" placeholder="6-digit pincode" data-testid="edit-account-pincode" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Clinic Potential *</Label>
                <div className="flex gap-3 mt-2">
                  {['High', 'Medium', 'Low'].map(opt => (
                    <button key={opt} type="button" onClick={() => setEditAccountData(p => ({ ...p, clinic_potential: opt }))}
                      className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        editAccountData.clinic_potential === opt
                          ? opt === 'High' ? 'bg-emerald-600 text-white border-emerald-600'
                          : opt === 'Medium' ? 'bg-amber-500 text-white border-amber-500'
                          : 'bg-red-500 text-white border-red-500'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                      }`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Competitor Brands Currently Used *</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {COMPETITOR_OPTIONS.map(opt => (
                    <button key={opt} type="button"
                      onClick={() => {
                        const curr = editAccountData.competitor_brands || [];
                        setEditAccountData(p => ({ ...p, competitor_brands: curr.includes(opt) ? curr.filter(x => x !== opt) : [...curr, opt] }));
                      }}
                      className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                        (editAccountData.competitor_brands || []).includes(opt) ? 'bg-teal-700 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                      }`}>
                      {opt}
                    </button>
                  ))}
                  <button type="button"
                    onClick={() => {
                      const curr = editAccountData.competitor_brands || [];
                      setEditAccountData(p => ({ ...p, competitor_brands: curr.includes('Others') ? curr.filter(x => x !== 'Others') : [...curr, 'Others'] }));
                    }}
                    className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                      (editAccountData.competitor_brands || []).includes('Others') ? 'bg-teal-700 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                    }`}>
                    Others
                  </button>
                </div>
                {(editAccountData.competitor_brands || []).includes('Others') && (
                  <Input value={editAccountData.competitor_brands_others || ''} onChange={e => setEditAccountData(p => ({ ...p, competitor_brands_others: e.target.value }))} placeholder="Enter other brand..." className="mt-2 rounded-xl" />
                )}
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Current Sales Value (Rs.)</Label>
                <Input type="number" value={editAccountData.current_sales_value || 0} onChange={e => setEditAccountData(p => ({ ...p, current_sales_value: parseFloat(e.target.value) || 0 }))} className="mt-1 h-12" data-testid="edit-account-sales-value" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditingAccount(false)}>Cancel</Button>
                <Button onClick={handleSaveAccount} disabled={savingAccount} className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white" data-testid="save-account-button">
                  {savingAccount ? 'Saving...' : localStorage.getItem('isAdmin')==='true' ? 'Save Changes' : 'Submit for Approval'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Contact Modal */}
        <Dialog open={!!editingContact} onOpenChange={(open) => { if (!open) setEditingContact(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Edit Contact</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={editContactData.name}
                    onChange={(e) => setEditContactData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-contact-name"
                  />
                </div>
                <div>
                  <Label>Designation *</Label>
                  <Select value={editContactData.designation} onValueChange={(v) => setEditContactData(prev => ({ ...prev, designation: v }))}>
                    <SelectTrigger className="mt-1" data-testid="edit-contact-designation">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {DESIGNATIONS.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>WhatsApp Number *</Label>
                  <Input
                    value={editContactData.whatsapp_number}
                    onChange={(e) => setEditContactData(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-contact-whatsapp"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={editContactData.email}
                    onChange={(e) => setEditContactData(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-contact-email"
                  />
                </div>
              </div>
              <div>
                <div>
                  <Label>Has Influence on Purchase? *</Label>
                  <Select value={editContactData.can_influence_decisions} onValueChange={(v) => setEditContactData(prev => ({ ...prev, can_influence_decisions: v }))}>
                    <SelectTrigger className="mt-1" data-testid="edit-contact-decision-maker">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditingContact(null)}>Cancel</Button>
                <Button
                  onClick={handleSaveContact}
                  disabled={savingContact}
                  className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white"
                  data-testid="save-contact-button"
                >
                  {savingContact ? 'Saving...' : localStorage.getItem('isAdmin')==='true' ? 'Save Changes' : 'Submit for Approval'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>


{/* Add New Contact Modal */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Add New Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Full Name *</label>
              <input
                value={newContactData.name}
                onChange={e => setNewContactData(p => ({ ...p, name: e.target.value }))}
                placeholder="Doctor's full name"
                className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Designation *</label>
              <select
                value={newContactData.designation}
                onChange={e => setNewContactData(p => ({ ...p, designation: e.target.value }))}
                className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-teal-500"
              >
                <option value="">Select designation</option>
                {['Clinic Owner (Orthodontist)', 'Clinic Owning (Non-Orthodontist)', 'Consultant Orthodontist', 'Others'].map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Phone Number *</label>
              <input
                value={newContactData.whatsapp_number}
                onChange={e => setNewContactData(p => ({ ...p, whatsapp_number: e.target.value }))}
                placeholder="Phone number(s)"
                className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Email (Optional)</label>
              <input
                type="email"
                value={newContactData.email}
                onChange={e => setNewContactData(p => ({ ...p, email: e.target.value }))}
                placeholder="Email address"
                className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Has Influence on Purchase? *</label>
              <div className="flex gap-3">
                {['Yes', 'No'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setNewContactData(p => ({ ...p, can_influence_decisions: opt }))}
                    className={`flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                      newContactData.can_influence_decisions === opt
                        ? opt === 'Yes' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-slate-600 text-white border-slate-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddContact(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleAddContact}
                disabled={addingContact}
                className="flex-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl"
              >
                {addingContact ? 'Adding...' : 'Add Contact'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Call Type Picker Modal */}
      {showCallTypePicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <h3 className="font-bold text-slate-800 text-lg mb-2">Register New Call</h3>
            <p className="text-sm text-slate-500 mb-6">What type of call would you like to register for <strong>{account?.name}</strong>?</p>
            <div className="space-y-3">
              <button type="button"
                onClick={() => { setShowCallTypePicker(false); navigate(`/calls/new?accountId=${accountId}`); }}
                className="w-full py-4 px-5 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl font-semibold flex items-center gap-3 hover:from-teal-800 hover:to-emerald-600 transition-all">
                <span className="text-2xl">🤝</span>
                <div className="text-left">
                  <p className="font-bold">Field Call</p>
                  <p className="text-xs text-white/80">In-person visit to the clinic</p>
                </div>
              </button>
              <button type="button"
                onClick={() => { setShowCallTypePicker(false); navigate(`/calls/telephonic?accountId=${accountId}`); }}
                className="w-full py-4 px-5 bg-white border-2 border-teal-200 text-teal-700 rounded-xl font-semibold flex items-center gap-3 hover:bg-teal-50 transition-all">
                <span className="text-2xl">📞</span>
                <div className="text-left">
                  <p className="font-bold">Telephonic Call</p>
                  <p className="text-xs text-teal-500">Phone/WhatsApp/video call</p>
                </div>
              </button>
            </div>
            <button type="button" onClick={() => setShowCallTypePicker(false)}
              className="w-full mt-4 py-2 text-slate-400 text-sm hover:text-slate-600">
              Cancel
            </button>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default AccountDetail;
